import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

export const useGetCompaniesQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_COMPANIES],
    queryFn: () => api.get(API_ROUTES.GET_COMPANIES),
  });
  return { data: data?.data?.data ?? [], isPending, isError, error };
};

export const useCreateCompanyMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) => api.post(API_ROUTES.CREATE_COMPANY, data),
      onSuccess: () => {
        showToast("success", "Company created successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_COMPANIES);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useUpdateCompanyMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) =>
        api.put(`${API_ROUTES.UPDATE_COMPANY}/${data?.id}`, data),
      onSuccess: () => {
        showToast("success", "Company updated successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_COMPANIES]);
        queryClient.invalidateQueries([API_ROUTES.GET_COMPANY_BY_ID]);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useDeleteCompanyMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) =>
        api.delete(`${API_ROUTES.DELETE_COMPANY.replace(":id", id)}`),
      onSuccess: () => {
        showToast("success", "Company deleted successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_COMPANIES]);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// On-demand NSW register lookup for the company form's Corporate License block.
// Pass { licenceNumber } to resolve one, or { searchText } to get candidates.
export const useLookupNswLicenseMutation = () => {
  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (params) =>
      api.get(API_ROUTES.LOOKUP_NSW_LICENSE, { params }),
    onError: (err) => {
      showToast(
        "error",
        err?.response?.data?.message || "NSW register lookup failed"
      );
    },
  });
  return { mutateAsync, isPending, isError, error };
};
